/**
 * Owner: Masa Bayu
 * File: hub-account-cancel.ts
 * Date:Time: 2026-10-22 13:10
 * Description: §457 F6-C — klien "Batalkan akaun" (§453) untuk qxread.
 *   Pola dipindahkan daripada klien yang SUDAH TERBUKTI di produksi
 *   (`qxeditor/src/shell/qxeditor-hub-auth.ts:181-212`):
 *   `DELETE {hub}/v1/accounts/{accountId}` + Bearer + `{ password }`.
 *
 *   Semantik §453 TIDAK diubah di sini — mesej hub dipulangkan melalui
 *   `describeAuthError()` (penterjemahan di sempadan paparan sahaja) supaya
 *   401/403/404/409 kekal sebab sebenar. Berjaya (200) → sesi setempat dibuang.
 */

import { getHubAuthAccountId, getHubAuthToken } from '../publication/hub-auth';
import { endHubSession, qxpHubOrigin } from '../publication/hub-sync';
import { describeAuthError } from './hub-error-copy';

type FetchInit = {
  readonly method?: string;
  readonly headers?: Record<string, string>;
  readonly body?: string;
};
type FetchLike = (input: string, init?: FetchInit) => Promise<Response>;

export type HubCancelAttempt =
  | {
      readonly ok: true;
      readonly email: string;
      readonly sessionsRemoved: number;
      readonly ordersRetained: number;
    }
  | { readonly ok: false; readonly status: number; readonly error: string };

function failure(status: number, message: string): HubCancelAttempt {
  return { ok: false, status, error: describeAuthError(message) };
}

/** Mesej hub (`{ ok:false, error:'…' }`); badan bukan JSON → teks generik. */
async function hubErrorMessage(res: Response): Promise<string> {
  try {
    const body = (await res.json()) as { error?: unknown };
    return typeof body.error === 'string' && body.error.length > 0
      ? body.error
      : `Request failed (${String(res.status)})`;
  } catch {
    return `Respons identiti bukan JSON (HTTP ${String(res.status)})`;
  }
}

/**
 * `password` ialah kata laluan akaun SEMASA (re-auth §453), bukan kata laluan
 * baharu. Google-only accounts tiada `passHash` → hub sentah 401; kerana itu
 * paparan mengawal akses ke borang ini melalui `hasPassword` (S3).
 */
export async function cancelHubAccount(
  password: string,
  fetchImpl: FetchLike = fetch,
  origin: string = qxpHubOrigin(),
): Promise<HubCancelAttempt> {
  const token = getHubAuthToken();
  const accountId = getHubAuthAccountId();
  if (token === null || accountId === null) return failure(0, 'tiada sesi qxread aktif');

  let res: Response;
  try {
    res = await fetchImpl(`${origin}/v1/accounts/${encodeURIComponent(accountId)}`, {
      method: 'DELETE',
      headers: { 'content-type': 'application/json', authorization: `Bearer ${token}` },
      body: JSON.stringify({ password }),
    });
  } catch {
    return failure(0, 'Pelayan identiti tidak dapat dicapai');
  }

  if (!res.ok) return failure(res.status, await hubErrorMessage(res));

  let record: Record<string, unknown>;
  try {
    const body = (await res.json()) as unknown;
    record = body !== null && typeof body === 'object' ? (body as Record<string, unknown>) : {};
  } catch {
    return failure(500, `Respons identiti bukan JSON (HTTP ${String(res.status)})`);
  }
  const result = record.result;
  if (record.ok !== true || result === null || typeof result !== 'object') {
    return failure(500, 'Respons identiti tiada token atau akaun');
  }
  const row = result as { email?: unknown; sessionsRemoved?: unknown; ordersRetained?: unknown };
  // §509 F4 — akaun dipadam: sesi + grant hub + muatan artifak setempat mesti pergi
  // sekali, bukan token sahaja.
  endHubSession();
  return {
    ok: true,
    email: typeof row.email === 'string' ? row.email : '',
    sessionsRemoved: typeof row.sessionsRemoved === 'number' ? row.sessionsRemoved : 0,
    ordersRetained: typeof row.ordersRetained === 'number' ? row.ordersRetained : 0,
  };
}
